// @flow strict
import * as React from 'react';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import {useSelector, useDispatch} from 'react-redux';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {ERROR_CODES} from 'src/components/authentication/errors.js';
import {AuthFlow} from 'src/components/authentication/auth-flow.js';
import {
  TitleMedium,
  BodySmall,
  BodyLarge,
  BodyMedium,
  SubTitleMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {
  selectTotpDetails,
  selectUserLoginId,
  selectUserEmail,
  selectUserNewPhoneNumber,
  selectCurrentAgency,
  selectOtpLimitExceeded,
  selectOtpVerificationError,
  selectUserPhoneNumber,
} from 'src/selectors/authentication';
import {
  verifyOtpForUser,
  OTPVerificationError,
} from 'src/action-creators/authentication';
import classify from 'src/utils/classify';
import css from './authenticator-app-registration.css';


type Props = {goToPage: (page: string, newMfaFlow: ?string) => void};

const AuthenticatorAppRegistration = ({goToPage}: Props): React.Node => {
  const {t} = useLabelTranslation();
  const {isExtension} = useEnvironmentContext();
  const [step, setStep] = React.useState(1);
  const {qr_code: qrCode, secret} = useSelector(selectTotpDetails) || {};

  const handleEnrollment = async () => {
    setStep(2);
  };

  return (
    <div className={classify(css.container, {[css.containerExt]: isExtension})}>
      {isExtension ? (
        <SubTitleMedium className={css.titleExt}>
          {t(
            'Register_twofactor_authenticator',
            'Register two-factor authenticator',
          )}
        </SubTitleMedium>
      ) : (
        <TitleMedium className={css.title}>
          {t(
            'Register_twofactor_authenticator',
            'Register two-factor authenticator',
          )}
        </TitleMedium>
      )}
      {step === 1 ? <StepOne /> : <StepTwo goToPage={goToPage} />}
      {step === 1 && (
        <Button
          onClick={handleEnrollment}
          size={isExtension ? 'small' : 'medium'}
          type="primary"
          disabled={!(qrCode && secret)}
        >
          {t('Next_step', 'Next step')}
        </Button>
      )}
    </div>
  );
};

const StepOne = (): React.Node => {
  const {t} = useLabelTranslation();
  const {isExtension} = useEnvironmentContext();
  const {qr_code: qrCode, secret} = useSelector(selectTotpDetails) || {};

  const handleLearnMoreClick = () => {
    const url =
      'https://support.sensehq.co/hc/en-us/articles/33678196381453-Setting-Up-an-Authenticator-App-for-Multi-Factor-Authentication';
    window.open(url, '_blank');
  };

  return (
    <div className={css.stepOneContainer}>
      <div className={css.stepTextContainer}>
        {isExtension ? (
          <BodyMedium color="secondary">
            {t(
              'Step1_Scan_the_QR_Code_using_a_thirdparty_authenticator_app_or_enter_the_key_manually',
              'Step-1: Scan the QR Code using a third-party authenticator app or enter the key manually.',
            )}
          </BodyMedium>
        ) : (
          <BodyLarge color="secondary">
            {t(
              'Step1_Scan_the_QR_Code_using_a_thirdparty_authenticator_app_or_enter_the_key_manually',
              'Step-1: Scan the QR Code using a third-party authenticator app or enter the key manually.',
            )}
          </BodyLarge>
        )}
      </div>
      <img
        src={qrCode}
        alt={t('QR_Code', 'QR Code')}
        className={classify(css.qrImg, {[css.qrImgExt]: isExtension})}
      />
      {isExtension ? (
        <BodyMedium color="secondary">
          {t(
            'or_try_manual_entry_using_the_key_below',
            'or try manual entry using the key below.',
          )}
        </BodyMedium>
      ) : (
        <BodyLarge color="secondary">
          {t(
            'or_try_manual_entry_using_the_key_below',
            'or try manual entry using the key below.',
          )}
        </BodyLarge>
      )}
      <div className={css.codeTextInput}>
        <Input
          locked
          placeholder=""
          readOnly
          size={isExtension ? 'small' : 'medium'}
          type="text"
          value={secret}
        />
      </div>
      <div className={classify(css.infoLink, {[css.infoLinkExt]: isExtension})}>
        {isExtension ? (
          <BodySmall>
            {t(
              'Don\'t_have_an_authenticator_app',
              'Don\'t have an authenticator app?',
            )}
          </BodySmall>
        ) : (
          <BodyMedium color="secondary">
            {t(
              'Don\'t_have_an_authenticator_app',
              'Don\'t have an authenticator app?',
            )}
          </BodyMedium>
        )}
        <Link
          onClick={handleLearnMoreClick}
          as={isExtension ? 'buttonTextExtraSmall' : 'buttonTextMedium'}
        >
          {t('Learn_more', 'Learn more')}
        </Link>
      </div>
    </div>
  );
};

const StepTwo = ({goToPage}): React.Node => {
  const {makeKey, t} = useLabelTranslation();
  const [value, setValue] = React.useState('');
  const {isExtension} = useEnvironmentContext();
  const dispatch = useDispatch();
  const phoneNumber = useSelector(selectUserPhoneNumber);
  const userEmail = useSelector(selectUserEmail);
  const loginId = useSelector(selectUserLoginId);
  const newPhoneNumber = useSelector(selectUserNewPhoneNumber);
  const agency = useSelector(selectCurrentAgency);
  const otpLimitExceeded = useSelector(selectOtpLimitExceeded);
  const apiError = useSelector(selectOtpVerificationError);
  const {
    error_type: errorType,
    pending_attempts: pendingAttempts,
    error: otpError,
  } = apiError || {};
  const isOtpLimitError = errorType === 'IncorrectOTP';

  const otpLimitErrorText = `${t(
    'Invalid_verification_code',
    'Invalid verification code',
  )} : ${pendingAttempts} ${t('attempts_left', 'attempts left')}`;
  const disableVerify = !value || value.length !== 6 || otpLimitExceeded;
  const [disabled, setDisabled] = React.useState(disableVerify);
  const handleVerifyOtp = async () => {
    setDisabled(true);
    const apiData = await dispatch(
      verifyOtpForUser(
        value,
        userEmail,
        loginId,
        newPhoneNumber,
        'TOTP',
        false,
      ),
    );
    if (apiData) {
      await dispatch(
        OTPVerificationError({
          error: '',
          status_code: 0,
          error_type: '',
          pending_attempts: 0,
        }),
      );
    }
    if (!phoneNumber && apiData) {
      goToPage(AuthFlow.TOTP.PHONE_REGISTRATION, 'TOTP');
    } else if (apiData) {
      goToPage(AuthFlow.TOTP.SIGN_IN, 'TOTP');
    }
    setDisabled(disableVerify);
  };

  const handleOtpChange = (e) => {
    const val = e.target.value;
    setValue(val);
    if (val && val.length === 6) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleVerifyOtp();
    }
  };

  return (
    <>
      <div className={css.stepTextContainer}>
        {isExtension ? (
          <BodyMedium color="secondary">
            {t(
              'Step2_Verify_the_configuration_by_entering_the_6digit_code_generated_by_the_thirdparty_authenticator_app',
              'Step-2: Verify the configuration by entering the 6-digit code generated by the third-party authenticator app.',
            )}
          </BodyMedium>
        ) : (
          <BodyLarge color="secondary">
            {t(
              'Step2_Verify_the_configuration_by_entering_the_6digit_code_generated_by_the_thirdparty_authenticator_app',
              'Step-2: Verify the configuration by entering the 6-digit code generated by the third-party authenticator app.',
            )}
          </BodyLarge>
        )}
      </div>
      <div>
        <Input
          label={t('Enter_verification_code', 'Enter verification code')}
          placeholder={t(
            'Enter_the_6digit_verification_code',
            'Enter the 6-digit verification code',
          )}
          size={isExtension ? 'small' : 'medium'}
          type="number"
          value={value}
          onChange={handleOtpChange}
          classNames={{wrapper: css.inputWrapper}}
          error={!!otpError}
          errorText={
            isOtpLimitError
              ? otpLimitErrorText
              : t(
                  makeKey(ERROR_CODES[otpError] || otpError),
                  ERROR_CODES[otpError] || otpError,
                )
          }
          onKeyDown={handleKeyDown}
        />
      </div>
      <Button
        onClick={handleVerifyOtp}
        size={isExtension ? 'small' : 'medium'}
        type="primary"
        disabled={disabled}
      >
        {t('Verify', 'Verify')}
      </Button>
    </>
  );
};

export default AuthenticatorAppRegistration;
